import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'; // NEW: Lock Icon

const TaskInformation = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 12px 15px;
  min-height: 80px;
  border-radius: 8px;
  max-width: 311px;
  background: white;
  margin-top: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  border: 1px solid #e0e0e0;
  position: relative; // Added for icon positioning
  
  .barcode-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 800;
    color: #2e7d32;
    margin-bottom: 2px;
  }

  .sku-text {
    font-size: 13px;
    font-weight: 700;
    color: #1976d2;
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  .desc-text {
    font-size: 12px;
    color: #444;
    line-height: 1.4;
    margin-bottom: 8px;
  }

  .location-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    color: #757575;
    background: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
  }
`;

const TaskCard = ({ item, index, isBarcodeEnabled, showAllLocations }) => {
  const profile = useSelector(state => state.auth);
  const assignedStoreName = profile?.profile?.assigned_store_id;
  
  const isDifferentStore = assignedStoreName && assignedStoreName !== "" && item.location?.id !== assignedStoreName;
  
  // 1. Hide card if not in "All Locations" mode and it's not our store
  if (!showAllLocations && isDifferentStore) {
    return null;
  }

  return (
    <Draggable 
      key={item.id} 
      draggableId={item.id} 
      index={index}
      // 2. DISABLE DRAG logic: If it belongs to another store, lock it.
      isDragDisabled={isDifferentStore} 
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          // Change cursor to 'not-allowed' when dragging is disabled
          style={{ 
            ...provided.draggableProps.style,
            cursor: isDifferentStore ? 'not-allowed' : 'grab' 
          }}
        >
          <TaskInformation 
            style={{ 
              borderLeft: isDifferentStore ? '4px solid #ed6c02' : 'none',
              background: isDifferentStore ? '#fafafa' : '#ffffff',
              opacity: isDifferentStore ? 0.7 : 1,
              // Visual "grayscale" effect for locked items
              filter: isDifferentStore ? 'grayscale(0.4)' : 'none'
            }}
          >
            {/* 3. Lock Icon & Warning UI */}
            {isDifferentStore && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                width: '100%', 
                alignItems: 'center',
                marginBottom: '6px'
              }}>
                <div style={{ color: '#ed6c02', fontSize: '9px', fontWeight: 'bold' }}>
                  ⚠️ Different Store Location
                </div>
                <LockOutlinedIcon style={{ fontSize: '14px', color: '#ed6c02' }} />
              </div>
            )}
            
            {isBarcodeEnabled && (
              <div className="barcode-wrapper" style={{ color: isDifferentStore ? '#757575' : '#2e7d32' }}>
                <QrCodeScannerIcon style={{ fontSize: '14px' }} />
                <span>{item.barcode || '-'}</span>
              </div>
            )}

            <div className="sku-text" style={{ color: isDifferentStore ? '#757575' : '#1976d2' }}>
                {item.sku || '-'}
            </div>

            <div className="desc-text">{item.product_desc}</div>
            
            <div className="location-badge" style={{ background: isDifferentStore ? '#fff3e0' : '#f5f5f5' }}>
              <LocationOnOutlinedIcon style={{ fontSize: '12px' }} />
              {item.location?.id || 'No Location'}
            </div>
          </TaskInformation>
        </div>
      )}
    </Draggable>
  );
};

export default TaskCard;